import { useCallback, useEffect, useRef, useState } from "react";
import { fetchBanners, uploadBanner, deleteBanner } from "../api";

export default function AdminBannerManager({ user }) {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [deletingId, setDeletingId] = useState(null);
    const [hrefInput, setHrefInput] = useState("/store");
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const fileInputRef = useRef(null);

    const loadBanners = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await fetchBanners();
            setBanners(data);
        } catch (err) {
            setError(err.message || "Failed to load banners");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadBanners();
    }, [loadBanners]);

    const handleUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setUploading(true);
        setError("");
        setMessage("");

        try {
            for (const file of files) {
                await uploadBanner(user.token, file, hrefInput);
            }
            setMessage(`${files.length} banner${files.length > 1 ? "s" : ""} uploaded successfully!`);
            await loadBanners();
        } catch (err) {
            setError(err.message || "Failed to upload banner");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleDelete = async (bannerId) => {
        if (!window.confirm("Delete this banner? This action cannot be undone.")) return;

        setDeletingId(bannerId);
        setError("");
        setMessage("");

        try {
            await deleteBanner(user.token, bannerId);
            setMessage("Banner deleted successfully.");
            setBanners((prev) => prev.filter((b) => b.id !== bannerId));
        } catch (err) {
            setError(err.message || "Failed to delete banner");
        } finally {
            setDeletingId(null);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text-light)" }}>
                Loading banners...
            </div>
        );
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <style>{`
                .banner-mgr-card {
                    position: relative;
                    border-radius: 12px;
                    overflow: hidden;
                    border: 1px solid var(--border);
                    background: #fafafa;
                    transition: box-shadow 0.2s, transform 0.2s;
                }
                .banner-mgr-card:hover {
                    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
                }
                .banner-mgr-card.drag-over {
                    border-color: var(--forest);
                    box-shadow: 0 0 0 2px rgba(57,109,79,0.2);
                }
                .banner-mgr-img {
                    width: 100%;
                    aspect-ratio: 1440/500;
                    object-fit: cover;
                    display: block;
                    background: #e8e8e8;
                }
                .banner-mgr-overlay {
                    position: absolute;
                    top: 0; right: 0; bottom: 0; left: 0;
                    background: rgba(0,0,0,0);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    transition: background 0.2s;
                    pointer-events: none;
                }
                .banner-mgr-card:hover .banner-mgr-overlay {
                    background: rgba(0,0,0,0.35);
                    pointer-events: auto;
                }
                .banner-mgr-delete-btn {
                    background: #dc3545;
                    color: white;
                    border: none;
                    border-radius: 10px;
                    padding: 10px 20px;
                    font-size: 0.9rem;
                    font-weight: 600;
                    cursor: pointer;
                    opacity: 0;
                    transform: translateY(8px);
                    transition: opacity 0.2s, transform 0.2s;
                    font-family: inherit;
                }
                .banner-mgr-card:hover .banner-mgr-delete-btn {
                    opacity: 1;
                    transform: translateY(0);
                }
                .banner-mgr-delete-btn:hover {
                    background: #a71d2a;
                }
                .banner-mgr-badge {
                    position: absolute;
                    top: 10px;
                    left: 10px;
                    background: rgba(0,0,0,0.6);
                    color: white;
                    font-size: 0.75rem;
                    font-weight: 700;
                    padding: 4px 10px;
                    border-radius: 6px;
                    backdrop-filter: blur(4px);
                    z-index: 2;
                }
                .banner-upload-zone {
                    border: 2px dashed var(--border);
                    border-radius: 16px;
                    padding: 40px 20px;
                    text-align: center;
                    cursor: pointer;
                    transition: border-color 0.2s, background 0.2s;
                    background: white;
                }
                .banner-upload-zone:hover {
                    border-color: var(--forest);
                    background: rgba(57,109,79,0.03);
                }
                .banner-upload-zone.uploading {
                    opacity: 0.6;
                    pointer-events: none;
                }

                @media (max-width: 640px) {
                    .banner-mgr-grid {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>

            {/* Header */}
            <div style={{
                background: "white",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: 20,
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
                flexWrap: "wrap",
                alignItems: "center",
            }}>
                <div>
                    <h2 style={{ margin: "0 0 6px 0", fontSize: "1.4rem" }}>🖼️ Banner Manager</h2>
                    <p style={{ margin: 0, color: "var(--text-light)" }}>
                        Upload, manage, and delete homepage hero banners. Recommended size: <strong>1440×500px</strong>.
                    </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.9rem", color: "var(--text-light)" }}>
                    <span style={{
                        background: "var(--forest)",
                        color: "white",
                        borderRadius: 999,
                        padding: "4px 12px",
                        fontWeight: 700,
                        fontSize: "0.85rem",
                    }}>
                        {banners.length} banner{banners.length !== 1 ? "s" : ""}
                    </span>
                </div>
            </div>

            {/* Status Messages */}
            {error && (
                <div style={{
                    background: "#fff4f4",
                    border: "1px solid #f3b5b5",
                    color: "#8b1e1e",
                    borderRadius: 12,
                    padding: "12px 16px",
                }}>
                    {error}
                </div>
            )}

            {message && (
                <div style={{
                    background: "#eef9f0",
                    border: "1px solid #b9dfc0",
                    color: "#235f2f",
                    borderRadius: 12,
                    padding: "12px 16px",
                }}>
                    {message}
                </div>
            )}

            {/* Upload Section */}
            <div style={{
                background: "white",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: 20,
                display: "flex",
                flexDirection: "column",
                gap: 16,
            }}>
                <h3 style={{ margin: 0, fontSize: "1.1rem" }}>Upload New Banners</h3>

                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 200px" }}>
                        <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-light)" }}>
                            Link destination (href)
                        </span>
                        <input
                            type="text"
                            value={hrefInput}
                            onChange={(e) => setHrefInput(e.target.value)}
                            placeholder="/store"
                            style={{
                                padding: "10px 12px",
                                borderRadius: 10,
                                border: "1px solid var(--border)",
                                fontFamily: "inherit",
                                fontSize: "0.9rem",
                            }}
                        />
                    </label>
                </div>

                <div
                    className={`banner-upload-zone ${uploading ? "uploading" : ""}`}
                    onClick={() => !uploading && fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOverIndex("upload"); }}
                    onDragLeave={() => setDragOverIndex(null)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragOverIndex(null);
                        if (e.dataTransfer.files?.length) {
                            const fakeEvent = { target: { files: e.dataTransfer.files } };
                            handleUpload(fakeEvent);
                        }
                    }}
                    style={dragOverIndex === "upload" ? { borderColor: "var(--forest)", background: "rgba(57,109,79,0.05)" } : {}}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleUpload}
                        style={{ display: "none" }}
                    />
                    {uploading ? (
                        <div>
                            <div style={{ fontSize: "2rem", marginBottom: 8 }}>⏳</div>
                            <p style={{ margin: 0, fontWeight: 600, color: "var(--forest)" }}>Uploading...</p>
                        </div>
                    ) : (
                        <div>
                            <div style={{ fontSize: "2rem", marginBottom: 8 }}>📤</div>
                            <p style={{ margin: "0 0 4px 0", fontWeight: 600 }}>Click or drag & drop banner images here</p>
                            <p style={{ margin: 0, color: "var(--text-light)", fontSize: "0.85rem" }}>
                                Supports JPEG, PNG, WebP — multiple files allowed
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Existing Banners Grid */}
            {banners.length === 0 ? (
                <div style={{
                    background: "white",
                    border: "1px solid var(--border)",
                    borderRadius: 16,
                    padding: "40px 20px",
                    textAlign: "center",
                    color: "var(--text-light)",
                }}>
                    <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>🏞️</div>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: "1.1rem" }}>No banners yet</p>
                    <p style={{ margin: "8px 0 0 0" }}>Upload your first banner above to get started.</p>
                </div>
            ) : (
                <>
                    {(() => {
                        const brokenBanners = banners.filter(b => b.image_url && b.image_url.includes('public.blob.vercel-storage.com'));
                        return brokenBanners.length > 0 ? (
                            <div style={{
                                background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: '10px',
                                padding: '12px 16px', color: '#721c24',
                                fontSize: '0.9rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '10px',
                            }}>
                                <span style={{ fontSize: '1.3rem' }}>⚠️</span>
                                <span>
                                    <strong>{brokenBanners.length} banner{brokenBanners.length > 1 ? 's' : ''}</strong> {brokenBanners.length > 1 ? 'have' : 'has'} images
                                    that were on Vercel Blob and are no longer accessible.
                                    Please delete them and re-upload new banners.
                                </span>
                            </div>
                        ) : null;
                    })()}
                    <div className="banner-mgr-grid" style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
                        gap: 16,
                    }}>
                        {banners.map((banner, idx) => {
                            const isBroken = banner.image_url && banner.image_url.includes('public.blob.vercel-storage.com');
                            return (
                                <div
                                    key={banner.id}
                                    className={`banner-mgr-card ${dragOverIndex === idx ? "drag-over" : ""}`}
                                    style={isBroken ? { border: '2px solid #dc3545' } : {}}
                                >
                                    <div className="banner-mgr-badge">#{idx + 1}</div>
                                    {isBroken ? (
                                        <div className="banner-mgr-img" style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexDirection: 'column', gap: '8px', background: '#fff3f3',
                                        }}>
                                            <span style={{ fontSize: '2.5rem' }}>⚠️</span>
                                            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#721c24' }}>
                                                Banner image lost — please delete and re-upload
                                            </span>
                                        </div>
                                    ) : (
                                        <img
                                            className="banner-mgr-img"
                                            src={banner.image_url}
                                            alt={`Banner ${idx + 1}`}
                                            draggable={false}
                                        />
                                    )}
                                    <div className="banner-mgr-overlay">
                                        <button
                                            className="banner-mgr-delete-btn"
                                            onClick={() => handleDelete(banner.id)}
                                            disabled={deletingId === banner.id}
                                        >
                                            {deletingId === banner.id ? "Deleting..." : "🗑️ Delete Banner"}
                                        </button>
                                    </div>
                                    <div style={{
                                        padding: "10px 14px",
                                        fontSize: "0.8rem",
                                        color: "var(--text-light)",
                                        borderTop: "1px solid var(--border)",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}>
                                        <span>Links to: <strong>{banner.href || "/store"}</strong></span>
                                        <span style={{ opacity: 0.5 }}>ID: {banner.id}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
